import cn from "classnames";
import endOfDay from "date-fns/endOfDay";
import dateFormat from "date-fns/format";
import isAfter from "date-fns/isAfter";
import isEqual from "date-fns/isEqual";
import isPast from "date-fns/isPast";
import startOfDay from "date-fns/startOfDay";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, memo, useMemo, useEffect } from "react";

import { useGetSet } from "@api/hooks/queries/useGetSet";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import EmptySlider from "@components/cms/EmptySlider/EmptySlider";
import { getPageFolderName } from "@config/cms";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import Button from "@ui-kit/Button/Button";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Countdown from "@ui-kit/Countdown/Countdown";
import DatePickerField from "@ui-kit/DatePicker/DayPicker";
import Tag from "@ui-kit/Tags/Tag";
import Tags from "@ui-kit/Tags/Tags";

import { routesCollectionName } from "./datas";
import s from "./PromoBlock.module.scss";

const CampaignRules = dynamic(
  () => import("@modals/CampaignRules/CampaignRules"),
  {
    ssr: false,
  },
);

const PROMO_ROUTE_QUERY_KEYS = ["slug"];

const getPlainCampaignCode = (value = "") => {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .replace(/(<([^>]+)>)/gi, "")
    .replace(/&nbsp;/g, " ")
    .trim();
};

const PromoBlock = ({ settings, updateField, data = {} }) => {
  const { t } = useTranslation();
  const { query } = useRouter();
  const { cms, modals, updateModals, updateCollectionType } = useStore();
  // const { isWidget } = useMousePosition();
  const { isWidgetEditing } = cms.widget;
  const { isWidget } = cms.page;
  const {
    title,
    content1,
    campaignCode,
    salesPeriodFrom,
    salesPeriodTo,
    travelPeriodFrom,
    travelPeriodTo,
    discount,
    collections,
    campaignRules,
  } = data;

  const { isEditMode } = cms.uiOptions;
  const canEditDates = isEditMode && !(!isWidgetEditing && isWidget);
  const { blockType } = settings.common;

  const { isOpenCampaignRulesModal } = modals;

  const { items, type } = collections[routesCollectionName];

  const [allItems, setAllItems] = useState([]);

  const cursorNotice = "Double click to edit routes";

  const memoizedItems = useMemo(() => {
    return collections[routesCollectionName].items.map((item) => ({ ...item }));
  }, [items]);

  const [getSet] = useGetSet({ lazy: true });

  const buyNowHref = useMemo(() => {
    const nextQuery = {};

    Object.entries(query).forEach(([key, value]) => {
      if (PROMO_ROUTE_QUERY_KEYS.includes(key) || value == null) {
        return;
      }

      nextQuery[key] = Array.isArray(value) ? value[value.length - 1] : value;
    });

    const promocodeFromUrl = String(nextQuery.promocode || "").trim();
    const campaignPromocode = getPlainCampaignCode(campaignCode);

    if (!promocodeFromUrl && campaignPromocode) {
      nextQuery.promocode = campaignPromocode;
    }

    if (!Object.keys(nextQuery).length) {
      return "/";
    }

    return {
      pathname: "/",
      query: nextQuery,
    };
  }, [query, campaignCode]);

  const toggleCampaignRulesModal = () => {
    updateModals({
      isOpenCampaignRulesModal: !isOpenCampaignRulesModal,
    });
  };

  const doNotRenderCountdown =
    isAfter(new Date(salesPeriodFrom), new Date(salesPeriodTo)) ||
    isPast(endOfDay(new Date(salesPeriodTo)));

  const handleClickToEdit = (e) => {
    switch (e.detail) {
      // case 1: // click
      // break;
      case 2: // double click
        updateCollectionType(e);
      // break;
      // case 3: // triple click
      // break;
    }
  };

  useEffect(() => {
    const promises = [];
    memoizedItems.forEach((item) => {
      if (item.setId) {
        promises.push(
          getSet({
            variables: {
              id: item.setId,
            },
          }),
        );
      }
    });

    Promise.all(promises).then((data) => {
      setAllItems([]);
      let counter = 0;
      memoizedItems.forEach((item) => {
        if (item.setId) {
          const setPages = data[counter].data.getSet.data.map((page) => {
            return {
              title: page.title,
              link: `/${getPageFolderName()[item.setType]}/${page.slug}`,
            };
          });

          setAllItems((items) => [...items, ...setPages]);

          counter += 1;

          return;
        }

        setAllItems((items) => [...items, item]);
      });
    });
  }, [memoizedItems]);

  return (
    <>
      {isOpenCampaignRulesModal && (
        <CampaignRules
          updateField={updateField}
          isOpen={isOpenCampaignRulesModal}
          toggleModal={toggleCampaignRulesModal}
          data={{ campaignRules }}
          isShowDivider={false}
        />
      )}
      <Container className={s.promo}>
        <Container full={false}>
          <section>
            <BlockTitle
              settings={settings}
              field={{ title }}
              onChange={updateField}
              type={blockType}
            />
            <div className={s.content}>
              <div className={cn(s.column, s.left)}>
                <Editor field={{ content1 }} onChange={updateField} />
                {!doNotRenderCountdown && (
                  <div className={s.countdown}>
                    {isAfter(
                      new Date(),
                      startOfDay(new Date(salesPeriodFrom)),
                    ) || isEqual(new Date(), new Date(salesPeriodFrom)) ? (
                      <>
                        <h5>{t("campaign ends in")}</h5>
                        <Countdown dateTo={endOfDay(new Date(salesPeriodTo))} />
                      </>
                    ) : (
                      <>
                        <h5>{t("campaign starts in")}</h5>
                        <Countdown
                          dateTo={startOfDay(new Date(salesPeriodFrom))}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className={cn(s.column, s.right)}>
                <div className={s.info}>
                  <div className={s.data}>
                    <div className={s.item}>
                      <div className={s.icon}>
                        <icon className={i.calendar} />
                      </div>
                      <div className={s.text}>
                        <div className={s.key}>{t("sales period")}</div>
                        <div className={s.value}>
                          {canEditDates ? (
                            <>
                              <DatePickerField
                                triggerElement={dateFormat(
                                  new Date(salesPeriodFrom),
                                  "dd.MM.yyyy",
                                )}
                                popperContentProps={{
                                  mode: "single",
                                  defaultMonth: salesPeriodFrom,
                                  selected: salesPeriodFrom,
                                  onSelect: (date) => {
                                    updateField({
                                      name: "salesPeriodFrom",
                                      value: date,
                                    });
                                  },
                                  disabled: [
                                    {
                                      before: new Date(),
                                    },
                                  ],
                                }}
                              />
                              &nbsp;—&nbsp;
                              <DatePickerField
                                triggerElement={dateFormat(
                                  new Date(salesPeriodTo),
                                  "dd.MM.yyyy",
                                )}
                                popperContentProps={{
                                  mode: "single",
                                  defaultMonth: salesPeriodTo,
                                  selected: salesPeriodTo,
                                  onSelect: (date) => {
                                    updateField({
                                      name: "salesPeriodTo",
                                      value: date,
                                    });
                                  },
                                  disabled: [
                                    {
                                      before: salesPeriodFrom || new Date(),
                                    },
                                  ],
                                  fromDate: salesPeriodFrom,
                                }}
                              />
                            </>
                          ) : (
                            <>
                              {dateFormat(
                                new Date(salesPeriodFrom),
                                "dd.MM.yyyy",
                              )}
                              &nbsp;—&nbsp;
                              {dateFormat(
                                new Date(salesPeriodTo),
                                "dd.MM.yyyy",
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={s.item}>
                      <div className={s.icon}>
                        <icon className={i.tag} />
                      </div>
                      <div className={s.text}>
                        <div className={s.key}>{t("campaign code")}</div>
                        <div className={s.value}>
                          <Editor
                            field={{ campaignCode }}
                            onChange={updateField}
                            onlyText
                          />
                        </div>
                      </div>
                    </div>
                    <div className={s.item}>
                      <div className={s.icon}>
                        <icon className={i.calendar} />
                      </div>
                      <div className={s.text}>
                        <div className={s.key}>{t("travel period")}</div>
                        <div className={s.value}>
                          {canEditDates ? (
                            <>
                              <DatePickerField
                                triggerElement={dateFormat(
                                  new Date(travelPeriodFrom),
                                  "dd.MM.yyyy",
                                )}
                                popperContentProps={{
                                  mode: "single",
                                  defaultMonth: travelPeriodFrom,
                                  selected: travelPeriodFrom,
                                  onSelect: (date) => {
                                    updateField({
                                      name: "travelPeriodFrom",
                                      value: date,
                                    });
                                  },
                                  disabled: [
                                    {
                                      before: new Date(),
                                    },
                                  ],
                                }}
                              />
                              &nbsp;—&nbsp;
                              <DatePickerField
                                triggerElement={dateFormat(
                                  new Date(travelPeriodTo),
                                  "dd.MM.yyyy",
                                )}
                                popperContentProps={{
                                  mode: "single",
                                  defaultMonth: travelPeriodTo,
                                  selected: travelPeriodTo,
                                  onSelect: (date) => {
                                    updateField({
                                      name: "travelPeriodTo",
                                      value: date,
                                    });
                                  },
                                  disabled: [
                                    {
                                      before: travelPeriodTo || new Date(),
                                    },
                                  ],
                                  fromDate: travelPeriodTo,
                                }}
                              />
                            </>
                          ) : (
                            <>
                              {dateFormat(
                                new Date(travelPeriodFrom),
                                "dd.MM.yyyy",
                              )}
                              &nbsp;—&nbsp;
                              {dateFormat(
                                new Date(travelPeriodTo),
                                "dd.MM.yyyy",
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={s.item}>
                      <div className={s.icon}>
                        <icon className={i.badgePercent} />
                      </div>
                      <div className={s.text}>
                        <div className={s.key}>{t("discount")}</div>
                        <div className={s.value}>
                          <Editor
                            field={{ discount }}
                            onChange={updateField}
                            onlyText
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className={s.routes}>
                    <h5>{t("routes")}</h5> */}
                  {items.length ? (
                    <div className={s.routes}>
                      <h5>{t("routes")}</h5>
                      <div
                        className={s.stopsWrap}
                        {...(isEditMode && !(!isWidgetEditing && isWidget)
                          ? {
                              "data-cursor-notice": cursorNotice,
                              "data-collection-type": type,
                              onClick: handleClickToEdit,
                            }
                          : {})}
                      >
                        <Tags>
                          {allItems.map((route, key) => {
                            const { title, slug } = route;
                            return (
                              <Tag
                                text={title}
                                to={`/${getPageFolderName().route}/${slug}/`}
                                key={key}
                              />
                            );
                          })}
                        </Tags>
                      </div>
                    </div>
                  ) : isEditMode ? (
                    <div className={s.routes}>
                      <h5>{t("routes")}</h5>
                      <EmptySlider
                        isSmall
                        cursorNotice={cursorNotice}
                        collectionType={type}
                        onClick={handleClickToEdit}
                      />
                    </div>
                  ) : null}
                  {/* </div> */}
                  <div className={s.controls}>
                    <Button
                      className={s.button}
                      to={buyNowHref}
                      text={t("buy now")}
                    />
                    <div
                      className={s.campaingRules}
                      onClick={toggleCampaignRulesModal}
                    >
                      <span>{t("campaign rules")}</span>
                      <icon className={i.arrowRight_v2} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Container>
      </Container>
    </>
  );
};

export default memo(PromoBlock);
